import { useViewport } from 'contexts/viewport';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Checkbox from 'shell/components/Checkbox';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import {
    ASSIGNMENT_SUBMISSION_TYPES,
    getAssignmentSubmissionTypes,
    toggleAssignmentSubmissionType,
    updateEvidencePreferences,
} from 'utils/assignmentSubmissionTypes';
import EvidencePreferencesDialog, { EVIDENCE_TYPE_DETAILS } from './EvidencePreferencesDialog';

const SubmissionTypeSettings = ({ form, setForm, disabled = false }) => {
    const { t } = useTranslation('assignmentSettings');
    const { device } = useViewport();
    const [dialogType, setDialogType] = useState(null);
    const selected = getAssignmentSubmissionTypes(form);

    return (
        <Stack spacing={3}>
            <Text component={'h3'} variant={'p1'} weight={'medium'}>
                {t('evidence.heading', 'What Will Students Submit? (Multi-Select)')}
            </Text>
            <Text>
                {t(
                    'evidence.description',
                    'This decides what the AI can look at, and what you are asked to configure below. Syncs with the accepted file types on the assignment.'
                )}
            </Text>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: device === 'mobile' ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
                    gap: 4,
                }}
            >
                {ASSIGNMENT_SUBMISSION_TYPES.map((type) => {
                    const config = EVIDENCE_TYPE_DETAILS[type];
                    const label = t(`evidence.types.${type}.label`, config.label);
                    const checked = selected.includes(type);
                    return (
                        <Box key={type} sx={{ position: 'relative', minWidth: 0 }}>
                            <Box
                                component={'label'}
                                sx={(theme) => ({
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    gap: theme.spacing(3),
                                    padding: theme.spacing(5),
                                    borderRadius: theme.spacing(4),
                                    cursor: disabled ? 'default' : 'pointer',
                                    border: `1px solid ${checked ? theme.palette.ensightful.primary[500] : theme.palette.ensightful.neutral[300]}`,
                                    background: checked
                                        ? theme.palette.ensightful.primary[50]
                                        : theme.palette.ensightful.shades[0],
                                    opacity: disabled ? 0.65 : 1,
                                    '&:focus-within': {
                                        outline: `2px solid ${theme.palette.ensightful.secondary[400]}`,
                                        outlineOffset: '2px',
                                    },
                                })}
                            >
                                <Stack direction={'row'} alignItems={'center'} spacing={3}>
                                    <Icon
                                        kind={config.icon}
                                        sx={{
                                            fontSize: '1.5rem',
                                            flexShrink: 0,
                                            color: (theme) => theme.palette.ensightful.neutral[700],
                                        }}
                                    />
                                    <Text weight={'medium'} sx={{ flex: 1, minWidth: 0, ...(checked && { pr: 9 }) }}>
                                        {label}
                                    </Text>
                                    <Checkbox
                                        aria-label={label}
                                        checked={checked}
                                        disabled={disabled}
                                        sx={{ p: 0, '& > input': { height: 24, width: 24 } }}
                                        onChange={() => {
                                            if (!disabled)
                                                setForm((current) => toggleAssignmentSubmissionType(current, type));
                                        }}
                                    />
                                </Stack>
                                <Text color={'light'}>
                                    {t(`evidence.types.${type}.description`, config.description)}
                                </Text>
                            </Box>
                            {checked && (
                                <IconButton
                                    aria-label={t('evidence.configure', 'Configure evidence for {{type}}', {
                                        type: label,
                                    })}
                                    kind={'settings'}
                                    size={'medium'}
                                    variant={'square'}
                                    disabled={disabled}
                                    sx={{
                                        position: 'absolute',
                                        right: (theme) => theme.spacing(14),
                                        top: (theme) => theme.spacing(3.5),
                                    }}
                                    onClick={() => setDialogType(type)}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>
            {dialogType && (
                <EvidencePreferencesDialog
                    type={dialogType}
                    preferences={form.gradingSettings?.evidence_preferences?.[dialogType]}
                    disabled={disabled}
                    onClose={() => setDialogType(null)}
                    onSave={(preferences) => {
                        if (disabled) return;
                        setForm((current) => updateEvidencePreferences(current, dialogType, preferences));
                        setDialogType(null);
                    }}
                />
            )}
        </Stack>
    );
};

export default SubmissionTypeSettings;
